import React, { useState } from "react";
import Loading from "../../ui/Loading";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import TRCourseTable from "./tr-course-table";
import getToken from "../../../hooks/getToken";
import Swal from "sweetalert2";
import { useLanguage } from "../../../store/language-context";
import { useTranslation } from "react-i18next";


const OnlineAndOffline = () => {
    const { URL } = useLanguage();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const selectedCityId = searchParams.get("city_id") || null;
    const { id } = useParams();
    const token = getToken();
    const navigate = useNavigate();

    const [section, setSection] = useState('off');
    const [cityId, setCityId] = useState(selectedCityId);

    const { data: cities, isLoading: cLoad, error: cErr } = useQuery({
        queryKey: ['cities-of-course', id, URL],
        queryFn: async () => {
            try {
                const response = await axios.get(`${URL}/cities?course_id=${id}`);
                const result = response?.data?.data?.data
                return result?.sort((a, b) => a.sort - b.sort);
            } catch (error) {
                throw new Error(t('custom.err'));
            }
        },
        retry: false
    });

    const { data: table, isLoading: tLoad, error: tErr } = useQuery({
        queryKey: ['city-distributions', id, cityId, URL],
        queryFn: async () => {
            try {
                const response = await axios.get(`${URL}/courses/${id}?city_id=${cityId}`);
                return response?.data?.data
            } catch (error) {
                throw new Error(t('custom.err'));
            }
        },
    });

    const firstHalfOfCities = cities?.length > 1 ? cities?.slice(0, cities?.length / 2) : cities
    const secondHalfOfCities = cities?.length > 6 ? cities.slice(cities.length / 2) : [];
    
    const isThereOnline = cities?.find(city => city?.id === 4);

    const bookASeat = useMutation({
        mutationKey: ['book-a-seat', id],
        mutationFn: async () => {
            if (!token) {
                setTimeout(() => {
                    Swal.fire({
                        icon: 'error',
                        title: t('custom.login-first'),
                        confirmButtonText: t('custom.accept'),
                        timer: 1500
                    });
                }, 500);
                setTimeout(() => {
                    navigate('/login')
                }, 2000);
                return;
            }
            const response = await axios.post(
                `${URL}/bookings`,
                { course_id: id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.status === 200) {
                Swal.fire({
                    icon: 'success',
                    title: t('custom.done'),
                    text: t('training-courses.chair-done'),
                    confirmButtonText: t('custom.accept')
                })
            }
        },
        onError: () => {
            Swal.fire({
                icon: 'error',
                title: t('custom.failed'),
                text: t('custom.err'),
                confirmButtonText: t('custom.accept')
            })
        }
    })
    return (
        <div className="my-4">
            <div className="flex items-start gap-4">
                <button
                    onClick={() => {
                        setSection('off');
                        setCityId(prev => prev === 4 && null)
                    }}
                    className={`${(section === 'off' && Number(selectedCityId) !== 4) ? 'training-btn-seen' : ''} py-2 px-4 after:hidden`} style={{ borderRadius: '8px 8px 0 0' }}>
                    {t('training-courses.inhouse')}
                </button>
                {
                    isThereOnline &&
                    <button
                        onClick={() => {
                            setSection('on');
                            setCityId(4)
                        }}
                        className={`${(section === 'on') || Number(selectedCityId) === 4 ? 'training-btn-seen' : ''} py-2 px-4 after:hidden`} style={{ borderRadius: '8px 8px 0 0' }}>
                        {t('training-courses.online')}
                    </button>
                }
            </div>
            <div className="border rounded-md p-3">
                {(section === 'off' && Number(selectedCityId) !== 4) &&
                    <div>
                        <p className="tr-title rtl:text-right ltr:text-left">
                            {t('training-courses.choose-date')}
                        </p>
                        <ul className="flex-wrap d-flex gap-3 py-3">
                            {cLoad &&
                                <Loading childrenNumber={8} childClass="w-full h-9 rounded-md">
                                    <div className="w-32"></div>
                                </Loading>}

                            {(cErr && table?.distribution?.length === 0) &&
                                <div>
                                    <buttton
                                        type="button"
                                        onClick={() => bookASeat.mutate()}
                                        className="text-white bg-secondary-tw text-lg px-4 py-2 my-4 rounded-md">
                                        {bookASeat.isPending ? `${t('training-courses.booking')}...` : t('training-courses.booking')}
                                    </buttton>
                                </div>
                            }

                            {(cErr && !table?.distribution?.length === 0) &&
                                <p className="text-red-500 text-lg">
                                    {t('custom.err')}
                                </p>
                            }

                            {
                                firstHalfOfCities?.map((city) => (
                                    city?.id !== 4 && <li key={city?.id}
                                        onClick={() => setCityId(city?.id)}
                                        className={`${city?.id === Number(cityId) ? 'bg-primary-tw text-white' : ''} px-4 border text-nowrap rounded-md cursor-pointer py-[1px]`}
                                    >
                                        {city?.id !== 4 && city?.title}
                                    </li>
                                ))

                            }
                        </ul>
                    </div>
                }

                {
                    cityId ?
                        tLoad ?
                            <div className="h-80 w-full grid rounded-md">
                                <div className="h-16 w-full l-load-dt rounded-md"></div>
                                <div className="h-16 w-full l-load-dt rounded-md"></div>
                                <div className="h-16 w-full l-load-dt rounded-md"></div>
                                <div className="h-16 w-full l-load-dt rounded-md"></div>
                            </div>
                            :
                            <TRCourseTable table={table?.distribution} error={tErr} isLoading={tLoad} />
                        : (
                            <TRCourseTable table={[]} error={tErr} isLoading={tLoad} />
                        )
                }

                {section === 'off' && (
                    <ul className="flex-wrap d-flex py-1 gap-3 pt-4">
                        {cLoad && (
                            <Loading childrenNumber={8} childClass={`w-full h-9 rounded-md`}>
                                <div className="w-32"></div>
                            </Loading>
                        )}
                        {secondHalfOfCities && secondHalfOfCities?.map((city) => (
                            city?.id !== 4 && <li key={city?.id}
                                onClick={() => setCityId(city?.id)}
                                className={`${city?.id === Number(cityId) ? 'bg-primary-tw text-white' : ''} px-4 border text-nowrap rounded-md cursor-pointer py-[1px]`}
                            >
                                {city?.id !== 4 && city?.title}
                            </li>
                        ))}
                    </ul>
                )}

            </div>
        </div>
    )
};

export default OnlineAndOffline;